<template>
  <div class="dashboard-container">
    <!-- 3D标题效果 -->
    <div class="title-container">
      <h1 class="title-3d" style="display: flex; justify-content: center">
        农情在线监测系统
        <div v-if="tabsData.activeTab > 0">
          - {{ tabsData.tabs[tabsData.activeTab].name }}
        </div>
      </h1>
      <div class="title-shadow"></div>
    </div>

    <!-- 时间显示 -->
    <div class="time-display">
      <div class="time">{{ currentTime }}</div>
      <div class="date">{{ currentDate }}</div>
    </div>

    <!-- 顶部页签导航 -->
    <div
      class="tab-container"
      style="display: flex; justify-content: center; margin-bottom: 10px"
    >
      <div
        v-for="(tab, index) in tabsData.tabs"
        :key="index"
        class="tab-item"
        @click="tabChange(index)"
      >
        <div class="button" :class="{ active: tabsData.activeTab === index }">
          {{ tab.name }}
        </div>
      </div>
    </div>

    <div v-show="tabsData.activeTab === 10">
      <!-- v-if="activeTab === 0"-->
      <!-- 数据卡片行 -->
      <div class="data-cards">
        <div v-for="(card, index) in cards" :key="index" class="card">
          <div class="card-icon" :style="{ backgroundColor: card.color }">
            <i :class="card.icon"></i>
          </div>
          <div class="card-content">
            <div class="card-title">{{ card.title }}</div>
            <div class="card-value" :style="{ color: card.color }">
              {{ card.value }}
            </div>
            <div class="card-trend">
              <span :style="{ color: card.trend > 0 ? '#f56c6c' : '#67c23a' }">
                {{ card.trend > 0 ? "↑" : "↓" }} {{ Math.abs(card.trend) }}%
              </span>
              同比
            </div>
          </div>
        </div>
      </div>

      <!-- 主内容区 -->
      <div class="main-content">
        <!-- 左侧图表区 -->
        <div class="left-section">
          <div class="chart-container">
            <div class="chart-title">访问量趋势</div>
            <div ref="lineChart" class="chart"></div>
          </div>
          <div class="chart-container">
            <div class="chart-title">设备占比</div>
            <div ref="pieChart" class="chart"></div>
          </div>
        </div>

        <!-- 中间表格区 -->
        <div class="center-section">
          <div class="table-container">
            <div class="table-title">实时数据监控</div>
            <el-table
              :data="tableData"
              :header-cell-style="{ background: '#1a3a6e', color: '#fff' }"
              :cell-style="{ background: '#0f2d5d', color: '#fff' }"
              stripe
              height="94%"
              style="background: #0f2d5d"
            >
              <el-table-column
                prop="id"
                label="ID"
                width="40"
                align="center"
              ></el-table-column>
              <el-table-column
                prop="name"
                label="名称"
                align="center"
              ></el-table-column>
              <el-table-column prop="status" label="状态" align="center">
                <template #default="scope">
                  <el-tag
                    :type="scope.row.status === '正常' ? 'success' : 'danger'"
                  >
                    {{ scope.row.status }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column
                prop="value"
                label="数值"
                align="center"
              ></el-table-column>
              <el-table-column prop="rate" label="增长率" align="center">
                <template #default="scope">
                  <span
                    :style="{
                      color: scope.row.rate > 0 ? '#f56c6c' : '#67c23a',
                    }"
                  >
                    {{ scope.row.rate > 0 ? "↑" : "↓" }}
                    {{ Math.abs(scope.row.rate) }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column
                prop="time"
                label="更新时间"
                :width="160"
                align="center"
              ></el-table-column>
            </el-table>
          </div>
        </div>

        <!-- 右侧图表区 -->
        <div class="right-section">
          <div class="chart-container">
            <div class="chart-title">任务完成率</div>
            <div ref="gaugeChart" class="chart"></div>
          </div>
          <div class="chart-container">
            <div class="chart-title">资源使用</div>
            <div ref="barChart" class="chart"></div>
          </div>
        </div>
      </div>
    </div>

    <div>
      <!-- <div v-show="tabsData.activeTab !== 0"> -->
      <el-table
        :data="filteredTableData"
        style="margin-bottom: 10px; background: #0f2d5d"
        :header-cell-style="{ background: '#1a3a6e', color: '#fff' }"
        :cell-style="{ background: '#0f2d5d', color: '#fff' }"
        stripe
        height="79vh"
      >
        <el-table-column
          v-for="(item, index) in tableDataList.title"
          :key="index"
          :label="item.name"
          :min-width="item.width || 100"
          align="center"
        >
          <template #default="scope">
            {{ scope.row.data[index] }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 底部滚动信息 -->
    <div class="scroll-info">
      <div class="scroll-content">
        {{ scrollText }} • {{ scrollText }} • {{ scrollText }} •
        {{ scrollText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import * as echarts from "echarts";
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { debounce } from "lodash";
import { nextTick } from "vue";

// 补零函数
const padZero = (num) => String(num).padStart(2, "0");

// 基于种子的伪随机数生成器（同一天内生成相同结果）
const seededRandom = (seed) => {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const generateRandomTimeWithinWeek = (index) => {
  const now = new Date();
  const oneWeekAgo = new Date(now);
  oneWeekAgo.setDate(now.getDate() - 7);

  const seed = index;
  const timeRange = now.getTime() - oneWeekAgo.getTime();
  const randomOffset = seededRandom(seed) * timeRange;
  const randomTime = new Date(oneWeekAgo.getTime() + randomOffset);

  if (randomTime > now) {
    randomTime.setTime(now.getTime() - 60 * 1000);
  }

  const year = randomTime.getFullYear();
  const month = padZero(randomTime.getMonth() + 1);
  const day = padZero(randomTime.getDate());
  const hours = padZero(randomTime.getHours());
  const minutes = padZero(randomTime.getMinutes());
  const seconds = padZero(randomTime.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// 生成动态时间（简化版，确保时间合理）
const generateDynamicTime = (daysAgo, hourOffset = 0, index = 0) => {
  const now = new Date();
  // 使用索引作为随机种子，确保同一条数据每次刷新时间固定
  const seed = index;

  // 计算基准日期（往前推 daysAgo 天）
  const baseDate = new Date(now);
  baseDate.setDate(now.getDate() - daysAgo);

  // 随机生成小时（8-22点之间，避免深夜时间）
  const randomHour = 8 + Math.floor(seededRandom(seed) * 14); // 8-22点
  // 随机生成分钟和秒
  const randomMinute = Math.floor(seededRandom(seed + 1) * 60);
  const randomSecond = Math.floor(seededRandom(seed + 2) * 60);

  baseDate.setHours(randomHour, randomMinute, randomSecond);

  // 如果是今天的日期，确保时间不晚于当前时间
  if (daysAgo === 0 && baseDate > now) {
    baseDate.setHours(now.getHours() - 1, now.getMinutes(), now.getSeconds());
  }

  // 格式化
  const year = baseDate.getFullYear();
  const month = String(baseDate.getMonth() + 1).padStart(2, "0");
  const day = String(baseDate.getDate()).padStart(2, "0");
  const hour = String(baseDate.getHours()).padStart(2, "0");
  const minute = String(baseDate.getMinutes()).padStart(2, "0");
  const second = String(baseDate.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

// 生成表格数据（动态时间）
const generateTableData = () => {
  return [
    // 气象设备
    {
      id: "1",
      data: [
        "1",
        "温湿度传感器",
        "QX-001",
        "QX001A001AA1",
        "在线",
        "温度",
        "25.6",
        generateDynamicTime(0, 0, 0),
        "℃",
        "-40",
        "80",
        "开启",
        "气象",
      ],
    },
    {
      id: "2",
      data: [
        "2",
        "温湿度传感器",
        "QX-002",
        "QX001A002BB2",
        "在线",
        "相对湿度",
        "65.2",
        generateDynamicTime(0, 0, 1),
        "%RH",
        "0",
        "100",
        "开启",
        "气象",
      ],
    },
    {
      id: "3",
      data: [
        "3",
        "风杯式风速风向传感器",
        "QX-004",
        "QX001A004DD4",
        "在线",
        "风速",
        "3.5",
        generateDynamicTime(0, 0, 3),
        "m/s",
        "0",
        "50",
        "开启",
        "气象",
      ],
    },
    {
      id: "4",
      data: [
        "4",
        "风杯式风速风向传感器",
        "QX-005",
        "QX001A005EE5",
        "在线",
        "风向",
        "180",
        generateDynamicTime(0, 0, 4),
        "°",
        "0",
        "360",
        "开启",
        "气象",
      ],
    },
    {
      id: "5",
      data: [
        "5",
        "称重式雨量计",
        "QX-006",
        "QX001A006FF6",
        "在线",
        "降雨量",
        "12.5",
        generateDynamicTime(0, 0, 5),
        "mm",
        "0",
        "500",
        "开启",
        "气象",
      ],
    },
    {
      id: "6",
      data: [
        "6",
        "光照时数传感器",
        "QX-008",
        "QX001A008HH8",
        "在线",
        "光照时长",
        "6.8",
        generateDynamicTime(0, 0, 7),
        "h",
        "0",
        "24",
        "开启",
        "气象",
      ],
    },
    {
      id: "7",
      data: [
        "7",
        "紫外线传感器",
        "QX-012",
        "QX001A012LL2",
        "在线",
        "紫外线指数",
        "3.5",
        generateDynamicTime(0, 0, 11),
        "UVI",
        "0",
        "15",
        "开启",
        "气象",
      ],
    },

    // 土壤监测站
    {
      id: "8",
      data: [
        "8",
        "土壤监测站",
        "TR-001",
        "TR001B001MM3",
        "在线",
        "土壤温度",
        "18.5",
        generateDynamicTime(0, 0, 12),
        "℃",
        "-20",
        "60",
        "开启",
        "土壤",
      ],
    },
    {
      id: "9",
      data: [
        "9",
        "土壤监测站",
        "TR-002",
        "TR001B002NN4",
        "在线",
        "土壤湿度",
        "45.2",
        generateDynamicTime(0, 0, 13),
        "%",
        "0",
        "100",
        "开启",
        "土壤",
      ],
    },
    {
      id: "10",
      data: [
        "10",
        "土壤监测站",
        "TR-003",
        "TR001B003OO5",
        "在线",
        "土壤氮/磷/钾",
        "6.8",
        generateDynamicTime(0, 0, 14),
        "mg",
        "0",
        "1999mg",
        "开启",
        "土壤",
      ],
    },
  ];
};

const tableDataList = ref({
  title: [
    { name: "序号", width: 55 },
    { name: "设备类型名称", width: 200 },
    { name: "设备类型编码", width: 120 },
    { name: "设备SN码", width: 150 },
    { name: "在线状态" },
    { name: "上传数据名称", width: 120 },
    { name: "上传数据值" },
    { name: "上传时间", width: 160 },
    { name: "单位" },
    { name: "设置最小值" },
    { name: "设置最大值" },
    // { name: "值类型" },
    { name: "数据开启状态", width: 120 },
  ],

  content: generateTableData(),
});

const tabsData = ref({
  activeTab: 0,
  tabs: [
    // { name: "首页", icon: "el-icon-s-home" },
    { name: "全部", icon: "el-icon-monitor" },
    { name: "气象", icon: "el-icon-connection" },
    { name: "土壤", icon: "el-icon-connection" },
  ],
});

const filteredTableData = computed(() => {
  const activeTab = tabsData.value.activeTab;
  const activeTabName = tabsData.value.tabs[activeTab].name;

  if (activeTabName === "首页" || activeTabName === "全部") {
    return tableDataList.value.content;
  }

  // 根据最后一个字段（分类）来筛选
  return tableDataList.value.content.filter((row) => {
    const category = row.data[row.data.length - 1];
    return category === activeTabName;
  });
});

// 当前时间
const currentTime = ref("");
const currentDate = ref("");

// 数据卡片
const cards = ref([
  {
    title: "总用户数",
    value: "12,845",
    trend: 12.5,
    icon: "el-icon-user",
    color: "#409EFF",
  },
  {
    title: "今日新增",
    value: "356",
    trend: 8.2,
    icon: "el-icon-circle-plus",
    color: "#67C23A",
  },
  {
    title: "访问数量",
    value: "2,845",
    trend: -3.4,
    icon: "el-icon-s-order",
    color: "#E6A23C",
  },
  {
    title: "总访问数",
    value: "128,450",
    trend: 22.1,
    icon: "el-icon-money",
    color: "#F56C6C",
  },
]);

// 表格数据
const tableData = ref([
  {
    id: 1,
    name: "水质仪1",
    status: "警告",
    value: "78%",
    rate: 2.5,
    time: generateDynamicTime(0, -2, 0),
  },
  {
    id: 2,
    name: "水质仪2",
    status: "正常",
    value: "92%",
    rate: 5.8,
    time: generateDynamicTime(1, 0, 1),
  },
  {
    id: 3,
    name: "虫情监测仪1",
    status: "正常",
    value: "45%",
    rate: -1.2,
    time: generateDynamicTime(0, -3, 2),
  },
  {
    id: 4,
    name: "虫情监测仪2",
    status: "正常",
    value: "32%",
    rate: 0.8,
    time: generateDynamicTime(2, 1, 3),
  },
  {
    id: 5,
    name: "电子秤1",
    status: "警告",
    value: "88%",
    rate: 12.5,
    time: generateDynamicTime(0, -1, 4),
  },
  {
    id: 6,
    name: "电子秤2",
    status: "正常",
    value: "65%",
    rate: -0.5,
    time: generateDynamicTime(3, -2, 5),
  },
  {
    id: 7,
    name: "增氧机1",
    status: "正常",
    value: "28%",
    rate: 1.2,
    time: generateDynamicTime(0, 0, 6),
  },
  {
    id: 8,
    name: "增氧机2",
    status: "正常",
    value: "35%",
    rate: 1.1,
    time: generateDynamicTime(1, 2, 7),
  },
  {
    id: 9,
    name: "增氧机3",
    status: "正常",
    value: "35%",
    rate: -1.3,
    time: generateDynamicTime(2, -1, 8),
  },
  {
    id: 10,
    name: "土壤监测仪1",
    status: "正常",
    value: "35%",
    rate: 2.0,
    time: generateDynamicTime(0, -4, 9),
  },
  {
    id: 11,
    name: "土壤监测仪2",
    status: "警告",
    value: "35%",
    rate: -2.4,
    time: generateDynamicTime(3, 3, 10),
  },
  {
    id: 12,
    name: "土壤监测仪3",
    status: "正常",
    value: "35%",
    rate: -1.2,
    time: generateDynamicTime(3, 3, 11),
  },
]);

// 图表引用
const lineChart = ref(null);
const pieChart = ref(null);
const barChart = ref(null);
const gaugeChart = ref(null);
const scrollText = ref("");

// 图表实例
const lineChartInstance = ref(null);
const pieChartInstance = ref(null);
const barChartInstance = ref(null);
const gaugeChartInstance = ref(null);

const initTableDataWithRandomTimes = () => {
  tableData.value.forEach((row, index) => {
    row._fixedTime = generateRandomTimeWithinWeek(index);
    row.time = row._fixedTime;
  });
};

// 只更新数值和增长率
const updateTableDataValues = () => {
  tableData.value.forEach((row) => {
    row.value = Math.floor(Math.random() * 30 + 20) + "%";
    row.rate = parseFloat((Math.random() * 10 - 5).toFixed(1));
    row.status = row.rate > 0 ? "正常" : "警告";
    row.time = row._fixedTime;
  });
  console.log("tableData", tableData);
};

// 更新时间
const updateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = padZero(now.getMonth() + 1);
  const day = padZero(now.getDate());
  const hours = padZero(now.getHours());
  const minutes = padZero(now.getMinutes());
  const seconds = padZero(now.getSeconds());

  const formattedDate = `${year}-${month}-${day}`;
  const formattedTime = `${hours}:${minutes}:${seconds}`;
  const weekdays = [
    "星期日",
    "星期一",
    "星期二",
    "星期三",
    "星期四",
    "星期五",
    "星期六",
  ];

  currentTime.value = formattedTime;
  currentDate.value = `${formattedDate} ${weekdays[now.getDay()]}`;
  scrollText.value = `系统运行正常 • 数据更新于${formattedDate} • 今日无异常报警 • 服务器负载正常`;
};

const scrollPosition = ref(0);
let scrollInterval = null;

// 初始化图表
const initCharts = () => {
  // console.log("initCharts(): lineChartInstance=", lineChartInstance.value);
  // // 折线图
  lineChartInstance.value.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
    },
    legend: {
      data: ["访问量", "注册量"],
      textStyle: {
        color: "#fff",
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: [
        "00:00",
        "03:00",
        "06:00",
        "09:00",
        "12:00",
        "15:00",
        "18:00",
        "21:00",
        "24:00",
      ],
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
    },
    yAxis: {
      type: "value",
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
      splitLine: {
        lineStyle: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
    series: [
      {
        name: "访问量",
        type: "line",
        smooth: true,
        data: [120, 132, 101, 134, 290, 230, 210, 182, 191],
        itemStyle: {
          color: "#409EFF",
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(64, 158, 255, 0.5)" },
            { offset: 1, color: "rgba(64, 158, 255, 0.1)" },
          ]),
        },
      },
      {
        name: "注册量",
        type: "line",
        smooth: true,
        data: [20, 32, 41, 34, 90, 130, 110, 82, 91],
        itemStyle: {
          color: "#67C23A",
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(103, 194, 58, 0.5)" },
            { offset: 1, color: "rgba(103, 194, 58, 0.1)" },
          ]),
        },
      },
    ],
  });

  // 饼图
  pieChartInstance.value.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
    },
    legend: {
      orient: "vertical",
      right: 10,
      top: "center",
      textStyle: {
        color: "#fff",
      },
    },
    series: [
      {
        name: "设备占比",
        type: "pie",
        radius: ["40%", "70%"],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#0f2d5d",
          borderWidth: 2,
        },
        label: {
          show: false,
          position: "center",
        },
        emphasis: {
          label: {
            show: true,
            fontSize: "18",
            fontWeight: "bold",
            color: "#fff",
          },
        },
        labelLine: {
          show: false,
        },
        data: [
          { value: 2, name: "水质仪", itemStyle: { color: "#409EFF" } },
          { value: 2, name: "虫情监测仪", itemStyle: { color: "#67C23A" } },
          { value: 2, name: "电子秤", itemStyle: { color: "#E6A23C" } },
          { value: 5, name: "增氧机", itemStyle: { color: "#F56C6C" } },
          { value: 6, name: "其他", itemStyle: { color: "#909399" } },
        ],
      },
    ],
  });

  // 柱状图
  barChartInstance.value.setOption({
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "value",
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
      splitLine: {
        lineStyle: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
    yAxis: {
      type: "category",
      data: ["CPU", "内存", "磁盘", "网络", "GPU"],
      axisLine: {
        lineStyle: {
          color: "#fff",
        },
      },
    },
    series: [
      {
        name: "使用率",
        type: "bar",
        data: [78, 65, 45, 32, 88],
        itemStyle: {
          color: function (params) {
            const colorList = [
              "#409EFF",
              "#67C23A",
              "#E6A23C",
              "#F56C6C",
              "#909399",
            ];
            return colorList[params.dataIndex];
          },
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  });

  // 仪表盘
  gaugeChartInstance.value.setOption({
    series: [
      {
        type: "gauge",
        progress: {
          show: true,
          width: 18,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "#67C23A" },
                { offset: 0.7, color: "#E6A23C" },
                { offset: 1, color: "#F56C6C" },
              ],
            },
          },
        },
        axisLine: {
          lineStyle: {
            width: 18,
            color: [[1, "rgba(255, 255, 255, 0.1)"]],
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          length: 3,
          lineStyle: {
            width: 2,
            color: "#fff",
          },
        },
        axisLabel: {
          distance: 20,
          color: "#fff",
          fontSize: 10,
        },
        anchor: {
          show: true,
          showAbove: true,
          size: 10,
          itemStyle: {
            borderWidth: 2,
            borderColor: "#fff",
          },
        },
        title: {
          show: false,
        },
        detail: {
          valueAnimation: true,
          fontSize: 22,
          offsetCenter: [0, "70%"],
          color: "#fff",
          formatter: "{value}%",
        },
        data: [{ value: 78.5 }],
      },
    ],
  });

  window.addEventListener("error", resizeObserverErrHandler);
  window.addEventListener("resize", handleResize);
};

const handleResize = debounce(() => {
  if (lineChartInstance.value) {
    // 使用 requestAnimationFrame 防止 ResizeObserver 循环
    requestAnimationFrame(() => {
      lineChartInstance.value.resize();
      pieChartInstance.value.resize();
      barChartInstance.value.resize();
      gaugeChartInstance.value.resize();
    });
  }
}, 100);

const resizeObserverErrHandler = (err) => {
  console.log("err", err);
  // 忽略 ResizeObserver 循环警告，这是浏览器的正常行为
  if (err.message.includes("ResizeObserver loop")) {
    // 可以选择记录到控制台但不抛出错误
    console.debug("ResizeObserver loop warning ignored");
    return;
  }
  // 其他错误正常处理
  console.error(err);
};

// 滚动信息动画
const startScroll = () => {
  scrollInterval = setInterval(() => {
    scrollPosition.value -= 1;
    if (scrollPosition.value < -scrollText.value.length * 10) {
      scrollPosition.value = 0;
    }
  }, 50);
};

const tabChange = (index) => {
  tabsData.value.activeTab = index;
};

// 组件挂载时初始化
onMounted(() => {
  nextTick(() => {
    lineChartInstance.value = echarts.init(lineChart.value);
    pieChartInstance.value = echarts.init(pieChart.value);
    barChartInstance.value = echarts.init(barChart.value);
    gaugeChartInstance.value = echarts.init(gaugeChart.value);

    updateTime();
    setInterval(updateTime, 1000);

    initCharts();
    startScroll();
    initTableDataWithRandomTimes();

    // 注册 ResizeObserver 错误处理器
    window.addEventListener("error", resizeObserverErrHandler);
    window.addEventListener("resize", handleResize);

    // 生成访问量趋势数据（模拟不同时间段起伏）
    const generateLineData = () => {
      const baseVisits = [120, 80, 60, 100, 180, 290, 320, 250, 150];
      const baseReg = [20, 15, 10, 25, 50, 90, 130, 95, 45];
      return {
        visits: baseVisits.map((v) =>
          Math.floor(v + (Math.random() - 0.5) * 40)
        ),
        reg: baseReg.map((v) => Math.floor(v + (Math.random() - 0.5) * 20)),
      };
    };

    // 更新图表数据（每5秒）
    const updateCharts = () => {
      // 更新折线图 - 访问量趋势
      const lineData = generateLineData();
      if (lineChartInstance.value) {
        lineChartInstance.value.setOption({
          series: [
            { name: "访问量", data: lineData.visits },
            { name: "注册量", data: lineData.reg },
          ],
        });
      }

      // 更新柱状图 - 资源使用（有浮动起伏）
      if (barChartInstance.value) {
        const resourceData = [78, 65, 45, 32, 88].map((v) =>
          Math.min(
            100,
            Math.max(10, Math.floor(v + (Math.random() - 0.5) * 20))
          )
        );
        barChartInstance.value.setOption({
          series: [{ data: resourceData }],
        });
      }
    };

    // 每日更新图表（任务完成率和用户分布）
    const dailyUpdateCharts = () => {
      // 更新仪表盘 - 任务完成率
      if (gaugeChartInstance.value) {
        const completionRate = parseFloat((60 + Math.random() * 35).toFixed(1));
        gaugeChartInstance.value.setOption({
          series: [{ data: [{ value: completionRate }] }],
        });
      }

      // 更新饼图 - 用户分布（值浮动较小）
      if (pieChartInstance.value) {
        const baseValues = [2, 2, 2, 5, 6];
        const total = baseValues.reduce((a, b) => a + b, 0);
        const newValues = baseValues.map((v) => {
          const variation = v * (Math.random() - 0.5) * 0.1; // 最多10%浮动
          return Math.max(100, Math.floor(v + variation));
        });
        // 归一化保持总和不变
        const newTotal = newValues.reduce((a, b) => a + b, 0);
        const normalizedValues = newValues.map((v) =>
          Math.floor((v * total) / newTotal)
        );
        // 调整最后一个值确保总和一致
        normalizedValues[4] =
          total - normalizedValues.slice(0, 4).reduce((a, b) => a + b, 0);

        pieChartInstance.value.setOption({
          series: [
            {
              data: [
                {
                  value: normalizedValues[0],
                  name: "水质仪",
                  itemStyle: { color: "#409EFF" },
                },
                {
                  value: normalizedValues[1],
                  name: "虫情监测仪",
                  itemStyle: { color: "#67C23A" },
                },
                {
                  value: normalizedValues[2],
                  name: "电子秤",
                  itemStyle: { color: "#E6A23C" },
                },
                {
                  value: normalizedValues[3],
                  name: "增氧机",
                  itemStyle: { color: "#F56C6C" },
                },
                {
                  value: normalizedValues[4],
                  name: "其他",
                  itemStyle: { color: "#909399" },
                },
              ],
            },
          ],
        });
      }
    };

    // 模拟实时数据更新（每5秒更新卡片、表格和部分图表）
    setInterval(() => {
      // 更新卡片数据
      cards.value.forEach((card) => {
        const change = Math.random() * 10 - 5;
        if (card.title === "总用户数") {
          const num = parseInt(card.value.replace(/,/g, ""));
          card.value = (num + Math.floor(Math.random() * 10)).toLocaleString();
        } else if (card.title === "今日新增") {
          const num = parseInt(card.value);
          card.value = (num + Math.floor(Math.random() * 5 - 2)).toString();
        } else if (card.title === "访问数量") {
          const num = parseInt(card.value.replace(/,/g, ""));
          card.value = (
            num + Math.floor(Math.random() * 20 - 10)
          ).toLocaleString();
        }
        card.trend = parseFloat((card.trend + change / 10).toFixed(1));
      });
      // 只更新表格的数值和增长率
      updateTableDataValues();
      // 更新图表数据（折线图和柱状图）
      updateCharts();
    }, 1000 * 600);

    // 每日更新（任务完成率和用户分布）
    dailyUpdateCharts();
    // 计算到下一天0点的时间差
    const nowTime = new Date();
    const nextDay = new Date(
      nowTime.getFullYear(),
      nowTime.getMonth(),
      nowTime.getDate() + 1
    );
    const timeToNextDay = nextDay.getTime() - nowTime.getTime();
    // 设置每日定时更新
    setTimeout(() => {
      dailyUpdateCharts();
      // 之后每天更新
      setInterval(dailyUpdateCharts, 24 * 60 * 60 * 1000);
    }, timeToNextDay);
  });
});

// 组件卸载前清除定时器
onBeforeUnmount(() => {
  console.log("onBeforeUnmount()");
  window.removeEventListener("error", resizeObserverErrHandler);
  window.removeEventListener("resize", handleResize);
  if (scrollInterval) {
    clearInterval(scrollInterval);
  }
});
</script>

<style scoped>
/* 基础样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.dashboard-container {
  width: 100%;
  height: 100vh;
  background: linear-gradient(to right, #0f1c3e, #0f2d5d);
  color: #fff;
  font-family: "Arial", sans-serif;
  overflow: hidden;
  padding: 20px;
  position: relative;
}

/* 3D标题效果 */
.title-container {
  position: relative;
  text-align: center;
  margin-bottom: 20px;
}

.title-3d {
  font-size: 42px;
  font-weight: bold;
  color: #fff;
  text-shadow: 0 5px 15px rgba(0, 150, 255, 0.5);
  transform: perspective(500px) rotateX(10deg);
  letter-spacing: 2px;
  position: relative;
  z-index: 1;
}

.title-shadow {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%) perspective(500px) rotateX(60deg) scaleY(0.8);
  width: 50%;
  height: 20px;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 150, 255, 0.5) 0%,
    rgba(0, 0, 0, 0) 70% /* transparent 100% */
  );
  filter: blur(5px);
  z-index: 0;
  will-change: transform;
}

/* 时间显示 */
.time-display {
  position: absolute;
  top: 20px;
  right: 20px;
  text-align: right;
}

.time {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.date {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
}

/* 数据卡片 */
.data-cards {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 10px;
}

.button {
  flex: 1;
  margin: 0 10px;
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(64, 158, 255, 0.2);
  height: 10px;
  margin-bottom: 10px;
  cursor: pointer;
}

.active {
  background: rgba(64, 158, 255, 0.3);
  color: #fff;
  font-weight: bold;
  border-bottom: 2px solid #409eff;
}

/* .tab-item:hover:not(.active) {
    background: rgba(64, 158, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  } */

.card {
  flex: 1;
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4);
  border-color: rgba(64, 158, 255, 0.5);
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 24px;
  color: #fff;
}

.card-content {
  flex: 1;
}

.card-title {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 5px;
}

.card-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.card-trend {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

/* 主内容区 */
.main-content {
  display: flex;
  height: calc(100vh - 320px);
  gap: 10px;
}

.left-section,
.right-section {
  width: 25%;
  display: flex;
  flex-direction: column;
}

.center-section {
  width: calc(50% + 8px);
}

.chart-container {
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 10px;
  height: 50%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.chart-title {
  font-size: 16px;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: bold;
}

.chart {
  width: 100%;
  height: 200px;
}

.table-container {
  overflow: hidden;
  background: rgba(16, 42, 90, 0.7);
  border-radius: 8px;
  padding: 15px;
  height: calc(100% - 10px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.table-title {
  font-size: 16px;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: bold;
}

/* 滚动信息 */
.scroll-info {
  width: 100%;
  height: 30px;
  background: rgba(16, 42, 90, 0.7);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.scroll-content {
  position: absolute;
  white-space: nowrap;
  line-height: 30px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .main-content {
    flex-direction: column;
    height: auto;
  }

  .left-section,
  .center-section,
  .right-section {
    width: 100%;
    margin: 0 0 10px 0;
  }

  .chart-container {
    height: 300px;
  }

  /* 新增的页签样式 */
  .tab-container {
    display: flex;
    background: rgba(16, 42, 90, 0.7);
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(64, 158, 255, 0.2);
    overflow: hidden;
  }

  .tab-item {
    flex: 1;
    padding: 12px 0;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tab-item i {
    margin-right: 8px;
    font-size: 16px;
  }
}
</style>
